import { useClickOutside } from "@mantine/hooks";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { AngleDownIcon } from "@/public/icons/AngleDownIcon";
import { AngleUpIcon } from "@/public/icons/AngleUpIcon";
import styles from "./Select.module.scss";

type SelectProps<T> = {
  initialIndex: number;
  setSelectedValue: (value: T) => void;
  selectOptions: T[];
  selectOptionsLabels: string[];
  groupBy?: string;
  placeholder?: string;
  isDisabled?: boolean;
  isError?: boolean;
};

export const setInitialValue = (
  initialValue: unknown,
  optionList: unknown[]
): number => {
  for (let i = 0; i < optionList.length; i++) {
    if (initialValue === optionList[i]) {
      return i;
    }
  }

  return -1;
};

export const Select = <T,>({
  initialIndex,
  setSelectedValue,
  selectOptions,
  selectOptionsLabels,
  groupBy,
  placeholder,
  isDisabled,
  isError,
}: SelectProps<T>) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(initialIndex);
  const [optionListOpened, setOptionListOpened] = useState<boolean>(false);
  const [groups, setGroups] = useState<unknown[]>();
  const ref = useClickOutside(() => setOptionListOpened(false));

  const setIsOpenedSelect = (value: boolean) => {
    if (!isDisabled) {
      setTimeout(() => {
        setOptionListOpened(value);
      }, 100);
    }
  };

  const setSelectedOption = (value: T, index: number) => {
    setSelectedIndex(index);
    setSelectedValue(value);
    setIsOpenedSelect(false);
  };

  useEffect(() => {
    setSelectedIndex(initialIndex);
  }, [initialIndex]);

  useEffect(() => {
    const temp: unknown[] = [];

    if (groupBy !== undefined) {
      selectOptions.map((option) => {
        if (!temp.includes(option[groupBy])) {
          temp.push(option[groupBy]);
        }
      });
    }

    setGroups(temp.sort());
  }, [groupBy, selectOptions]);

  return (
    <div className={styles.selectInputGroup}>
      <div className={styles.inputGroup}>
        <input
          className={classNames(styles.selectInput, {
            [styles.inputDisabled]: isDisabled,
          })}
          disabled={isDisabled}
          placeholder={placeholder}
          value={selectOptionsLabels[selectedIndex] ?? ""}
          readOnly
          onClick={() => setIsOpenedSelect(true)}
        />
        <div
          className={classNames(styles.optionList, {
            [styles.visible]: optionListOpened,
          })}
          ref={ref}
        >
          {groupBy !== undefined &&
            groups?.map((group) => (
              <div className={styles.groupOption} key={group as string}>
                <div className="groupLabel">{group as string}</div>
                {selectOptions.map((option, index) => {
                  if (option[groupBy] === group) {
                    return (
                      <div
                        className={styles.option}
                        onClick={() => setSelectedOption(option, index)}
                        key={index}
                      >
                        {selectOptionsLabels[index]}
                      </div>
                    );
                  }
                })}
              </div>
            ))}
          {groupBy === undefined &&
            selectOptions.map((option, index) => (
              <div
                className={styles.option}
                onClick={() => setSelectedOption(option, index)}
                key={index}
              >
                {selectOptionsLabels[index]}
              </div>
            ))}
        </div>
        <div
          className={styles.arrow}
          onClick={() => setIsOpenedSelect(!optionListOpened)}
        >
          {optionListOpened && (
            <AngleUpIcon
              colorClass={classNames({
                [styles.arrowColor]: !isError && !isDisabled,
                [styles.arrowDisabled]: !isError && isDisabled,
                [styles.arrowError]: isError && !isDisabled,
              })}
            />
          )}
          {!optionListOpened && (
            <AngleDownIcon
              colorClass={classNames({
                [styles.arrowColor]: !isError && !isDisabled,
                [styles.arrowDisabled]: !isError && isDisabled,
                [styles.arrowError]: isError && !isDisabled,
              })}
            />
          )}
        </div>
      </div>
    </div>
  );
};
